<script setup lang="ts">
import { useQueryClient } from '@tanstack/vue-query'
import { useField, useForm } from 'vee-validate'
import * as yup from 'yup'
import { type CreateAdminPayload, createAdminAction } from '@/api/functions/access/create-admin-action'
import { useMutationApi } from '@/composables/useApi'
import { useToast } from '@/composables/useToast'
import { getErrorMessage } from '@/utils/errorUtils'

defineProps({
  request: { type: Object },
  rolesOptions: { type: Array, default: () => [] },
})

const emit = defineEmits(['update:modelValue', 'confirm', 'cancel'])
const toast = useToast()
const queryClient = useQueryClient()

// --- Validation Schema ---
const schema = yup.object({
  full_name: yup.string().required('نام و نام خانوادگی الزامی است').min(3, 'حداقل ۳ کاراکتر').max(100),
  email: yup.string().required('ایمیل الزامی است').email('فرمت ایمیل معتبر نیست'),
  password: yup.string().required('رمز عبور الزامی است').min(8, 'حداقل 8 کاراکتر'),
  password_confirmation: yup
    .string()
    .required('تکرار رمز عبور الزامی است')
    .oneOf([yup.ref('password')], 'رمز عبور و تکرار آن باید یکسان باشند'),
  roles: yup.number().required('انتخاب نقش الزامی است'),
  status: yup.string().required('وضعیت عضو الزامی است'),
})

// --- Form setup ---
const { errors, resetForm, handleSubmit } = useForm<CreateAdminPayload>({
  validationSchema: schema,
  initialValues: {
    full_name: '',
    email: '',
    password: '',
    password_confirmation: '',
    roles: undefined as unknown as number,
    status: 'ACTIVATE',
  },
})

// Fields
const { value: full_name } = useField('full_name')
const { value: email } = useField('email')
const { value: password } = useField('password')
const { value: password_confirmation } = useField('password_confirmation')
const { value: roles } = useField('roles')
const { value: status } = useField('status')

// --- Mutation ---
const createMemberMutation = useMutationApi({
  mutationFn: (payload: CreateAdminPayload) => createAdminAction(payload),
  onSuccess: () => {
    toast.success('عضو جدید با موفقیت ایجاد شد', 'موفقیت')
    emit('confirm')
    closeModal()
    queryClient.invalidateQueries({ queryKey: ['admins'] }) // رفرش لیست اعضا
  },
  onError: (error: Error) => {
    toast.error(getErrorMessage(error), 'خطا')
  },
})

// --- Submit ---
const onSubmit = handleSubmit((values) => {
  createMemberMutation.mutate(values as CreateAdminPayload)
})

const closeModal = () => {
  emit('update:modelValue', false)
  emit('cancel')
  resetForm()
}
</script>

<template>
  <Modal @update:model-value="$emit('update:modelValue', $event)" width="600px" background="#FFF" :closable="true"
    title="افزودن عضو جدید" closeDirection="left" class="update-request">

    <Form @submit.prevent="onSubmit" class="flex flex-col min-h-[430px]">
      <div class="flex-1">
        <div class="lg:grid grid-cols-2 px-4 pt-6 pb-9 gap-6">
          <Input v-model="full_name" name="full_name" label="نام و نام خانوادگی را وارد کنید" type="text" />
          <Input v-model="email" name="email" label="ایمیل را وارد کنید" type="email" />
          <Input v-model="password" name="password" label="رمز ورود را وارد کنید" type="text" />
          <Input v-model="password_confirmation" name="password_confirmation" label="تکرار رمز ورود را وارد کنید"
            type="text" />
          <Select variant="checkbox" v-model="roles" name="roles" :error="errors.roles" label="نقش را انتخاب کنید"
            :options="rolesOptions" />
          <Select variant="checkbox" v-model="status" label="وضعیت عضو را مشخص کنید" :options="[
            { label: 'فعال', value: 'ACTIVATE' },
            { label: 'غیر فعال', value: 'DEACTIVATE' }
          ]" />
        </div>
      </div>

      <div class="sticky bottom-0 bg-white z-50 flex justify-between gap-6 w-full pt-3 pb-9 px-2 border-gray-200">
        <Button type="submit" label="تایید" :isAdmin="true" class="w-9/12 h-12 rounded-xl"
          :disabled="createMemberMutation.isPending.value" :loading="createMemberMutation.isPending.value" />
        <Button type="button" @click="closeModal" variant="outline" label="بازگشت" :isAdmin="true"
          class="w-4/12 h-12 rounded-xl border-2" :disabled="createMemberMutation.isPending.value" />
      </div>
    </form>
  </Modal>
</template>
